import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import supabaseService from '../services/supabaseService';
import { formatAmount } from '../utils/money';
import '../assets/styles/booking_form.css';

// She banks with Kiwibank; this is the account the transfer needs to land in.
const BANK_ACCOUNT = '38-9019-0475939-00';
const BANK_NAME = 'V Eggers T/A Beauty by Viktoria';

// A copy button next to anything that has to be retyped into a banking app on
// the same phone. Falls back to selecting nothing rather than throwing — an
// insecure context or a browser without the clipboard API just leaves the
// number on screen to read, which is what they'd have had anyway.
const CopyField = ({ label, value, hint, copyable = true }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Nothing to recover — the value is visible on screen.
    }
  };

  return (
    <div className="pay-row">
      <div className="pay-row-text">
        <span className="pay-label">{label}</span>
        <span className="pay-value">{value}</span>
        {hint && <span className="pay-hint">{hint}</span>}
      </div>
      {/* No button when there is nothing real to put on the clipboard. A Copy
          that copies an instruction, or nothing at all, is worse than no Copy:
          she pastes it into her banking app and never looks again. */}
      {copyable && (
        <button type="button" className="pay-copy" onClick={handleCopy} aria-label={`Copy ${label}`}>
          {copied ? <Check size={16} /> : <Copy size={16} />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      )}
    </div>
  );
};

const GiftVoucherForm = () => {
  const [formData, setFormData] = useState({
    purchaser_name: '',
    purchaser_email: '',
    purchaser_phone: '',
    recipient_name: '',
    amount: '',
    // Not a choice any more. Once the payment lands the voucher is minted and
    // emailed, full stop — there is no pickup path on the admin side to honour,
    // and asking someone to commit to a collection date before telling them it
    // will be emailed was the worst of both. The column is NOT NULL with a
    // check constraint of ('pickup','email'), so it still has to be sent.
    delivery_method: 'email',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (parseFloat(formData.amount) < 25) {
      setError('Minimum voucher amount is $25.');
      return;
    }

    setLoading(true);
    try {
      await supabaseService.createGiftVoucher(formData);
      await supabaseService.sendNotification('voucher_submitted', formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Gift voucher submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    // required only stops an empty box, not a box with a space in it, and a
    // blank reference printed under a heading that says "Reference" reads as
    // "leave it blank". Falls back to the same words the confirmation email
    // uses, so the two never say different things.
    const typedName = formData.purchaser_name.trim();
    const reference = typedName || 'your own name';
    // Normalised, not echoed: this is the number she is asked to transfer, and
    // "$0150" in a banking app is a moment of doubt nobody needs.
    const amount = formData.amount ? `$${formatAmount(formData.amount)}` : 'the voucher amount';

    return (
      <div className="booking-form-container">
        <div className="booking-form-custom">
          <div className="voucher-thanks">
            <h2>Thank you — I've got your request</h2>
            <p>
              The last step is the payment. I don't take cards for vouchers, because the
              card fee comes off a gift, so it's a bank transfer straight to me.
            </p>
          </div>

          <div className="pay-panel">
            <h3 className="pay-panel-title">Please transfer {amount} to</h3>

            <CopyField label="Account number" value={BANK_ACCOUNT} />
            <CopyField label="Account name" value={BANK_NAME} />
            <CopyField
              label="Reference"
              value={reference}
              copyable={!!typedName}
              hint="Your own name — that's how I match the payment to your voucher."
            />
          </div>

          <div className="voucher-thanks voucher-thanks--after">
            <p>
              Once I see the money land I'll make the voucher up and email it straight to
              you, ready to print or forward on. Nothing is issued before then, so if the
              transfer takes a day or two over a weekend, that's normal.
            </p>
            <p className="voucher-thanks-signoff">
              If anything looks wrong, or you'd like it sent to someone else, just get in
              touch and I'll sort it. — Viktoria
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-form-container">
      <form className="booking-form-custom" onSubmit={handleSubmit}>
        <div className="form-section">
          <h2 className="form-section-title">Your Details</h2>

          <div className="form-field">
            <label>Full Name *</label>
            <input
              type="text"
              name="purchaser_name"
              value={formData.purchaser_name}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-field">
            <label>Email *</label>
            <input
              type="email"
              name="purchaser_email"
              value={formData.purchaser_email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-field">
            <label>Phone *</label>
            <input
              type="tel"
              name="purchaser_phone"
              value={formData.purchaser_phone}
              onChange={handleChange}
              placeholder="Your phone number"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h2 className="form-section-title">Voucher Details</h2>

          <div className="form-field">
            <label>Who is this for? (optional)</label>
            <input
              type="text"
              name="recipient_name"
              value={formData.recipient_name}
              onChange={handleChange}
              placeholder="Recipient's name"
            />
          </div>

          <div className="form-field">
            <label>Voucher Amount ($) *</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="$25 minimum"
              min="25"
              step="1"
              required
            />
            {/* Said here, before anyone transfers a cent. The three years used
                to appear for the first time in the voucher email, which lands
                after the money has already gone. */}
            <p className="voucher-terms-note">
              Vouchers can be used for three years from the day I make them up, and
              they don't have to go all at once. If a treatment costs less than the
              voucher, the rest stays on it for next time.
            </p>
          </div>

          <div className="form-field">
            <label>Notes (optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any special requests or messages..."
              rows="3"
            />
          </div>
        </div>

        {error && (
          <div style={{ color: '#c0392b', marginBottom: '16px', fontSize: '0.9rem' }}>{error}</div>
        )}

        <button
          type="submit"
          className="submit-btn"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Request Gift Voucher'}
        </button>
      </form>
    </div>
  );
};

export default GiftVoucherForm;
